import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Mail, 
  ArrowLeft, 
  CheckCircle, 
  Loader2,
  Key,
  Eye,
  EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/functions/cn';

const emailSchema = z.object({
  email: z.string().email('Email inválido').min(1, 'Email é obrigatório')
});

const resetSchema = z.object({
  code: z.string().min(6, 'Código deve ter 6 dígitos').max(6, 'Código deve ter 6 dígitos'),
  password: z.string().min(8, 'Senha deve ter pelo menos 8 caracteres'),
  confirmPassword: z.string().min(8, 'Confirmação de senha é obrigatória')
}).refine((data) => data.password === data.confirmPassword, {
  message: "Senhas não coincidem",
  path: ["confirmPassword"],
});

type EmailFormData = z.infer<typeof emailSchema>;
type ResetFormData = z.infer<typeof resetSchema>;

type Step = 'email' | 'code' | 'success';

export const PasswordResetForm = () => {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema)
  });

  const resetForm = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema)
  });

  const handleEmailSubmit = async (data: EmailFormData) => {
    setIsLoading(true);
    try {
      // Mock API call for password reset
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setEmail(data.email);
      setStep('code');
      toast.success('Código de recuperação enviado para seu email');
    } catch (error) {
      toast.error('Erro ao enviar código de recuperação');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (data: ResetFormData) => {
    setIsLoading(true);
    try {
      // Mock API call for password reset
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setStep('success');
      toast.success('Senha alterada com sucesso!');
    } catch (error) {
      toast.error('Erro ao alterar senha');
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      // Mock API call to resend code
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Código reenviado para seu email');
    } catch (error) {
      toast.error('Erro ao reenviar código');
    }
  };

  if (step === 'success') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-green-600 dark:text-green-400">
            Senha Alterada!
          </CardTitle>
          <CardDescription>
            Sua senha foi alterada com sucesso
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={() => window.location.href = '/auth/sign-in'} 
            className="w-full"
          >
            Fazer Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === 'code') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setStep('email')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Key className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle className="text-center">Nova Senha</CardTitle>
          <CardDescription className="text-center">
            Digite o código enviado para {email} e sua nova senha
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={resetForm.handleSubmit(handleResetSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Código de Verificação</Label>
              <Input
                id="code"
                placeholder="000000"
                maxLength={6}
                className={cn(
                  "text-center font-mono tracking-wider",
                  resetForm.formState.errors.code && "border-red-500"
                )}
                {...resetForm.register('code')}
              />
              {resetForm.formState.errors.code && (
                <p className="text-sm text-red-500">{resetForm.formState.errors.code.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Digite sua nova senha"
                  className={resetForm.formState.errors.password ? "border-red-500" : ""}
                  {...resetForm.register('password')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {resetForm.formState.errors.password && (
                <p className="text-sm text-red-500">{resetForm.formState.errors.password.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirme sua nova senha"
                  className={resetForm.formState.errors.confirmPassword ? "border-red-500" : ""}
                  {...resetForm.register('confirmPassword')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {resetForm.formState.errors.confirmPassword && (
                <p className="text-sm text-red-500">{resetForm.formState.errors.confirmPassword.message}</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !resetForm.formState.isValid}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Alterando...
                </>
              ) : (
                'Alterar Senha'
              )}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              <p>Não recebeu o código?</p>
              <Button variant="link" onClick={resendCode} className="p-0 h-auto font-normal">
                Reenviar código
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Mail className="h-6 w-6 text-primary" />
        </div>
        <CardTitle>Recuperar Senha</CardTitle>
        <CardDescription>
          Digite seu email para receber um código de recuperação
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              className={emailForm.formState.errors.email ? "border-red-500" : ""}
              {...emailForm.register('email')}
            />
            {emailForm.formState.errors.email && (
              <p className="text-sm text-red-500">{emailForm.formState.errors.email.message}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || !emailForm.formState.isValid}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              'Enviar Código'
            )}
          </Button>

          <div className="text-center text-sm text-muted-foreground">
            <p>Lembrou da senha?</p>
            <Button variant="link" className="p-0 h-auto font-normal">
              <a href="/auth/sign-in">Voltar ao login</a>
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
